import api from './client';
import { getDashboardOverview, getSafetyAnalytics } from '../../api/client'; // Keep for now - helper functions
import { ENDPOINTS } from '../../config/api.config';
import logger from '../../utils/logger';

class IntegrationService {
  constructor() {
    // @VH_DELETE_CANDIDATE: UnifiedApiClient removed - using canonical client | delete_after=2026-02-15
    // Note: Some methods (getCachedData, createWebSocket, etc.) need to be refactored
    this.client = api; // Use canonical client
    this.endpoints = ENDPOINTS; // Use centralized endpoints
  }

  defineEndpoints() {
    // Kept for backward compatibility, but returns centralized endpoints
    return ENDPOINTS;
  }

  // Authentication methods
  async login(email, password, mfaCode = null) {
    const response = await this.client.post(this.endpoints.auth.login, {
      email,
      password,
      mfaCode,
    });

    // @VH_DELETE_CANDIDATE: storeToken removed - canonical client handles auth automatically | delete_after=2026-02-15
    // Token storage is handled by Firebase Auth + canonical client automatically
    // if (response.accessToken) {
    //   await this.client.storeToken(response.accessToken);
    // }

    return response;
  }

  async logout() {
    try {
      await this.client.post(this.endpoints.auth.logout);
    } finally {
    // @VH_DELETE_CANDIDATE: clearTokens/clearCache removed - canonical client handles auth | delete_after=2026-02-15
    // Token clearing is handled by Firebase Auth automatically
    }
  }

  async register(userData) {
    return this.client.post(this.endpoints.auth.register, userData);
  }

  // Dashboard methods
  async getDashboardData(role, timeRange = '7d') {
    // @VH_DELETE_CANDIDATE: Cache methods removed - use React Query for caching | delete_after=2026-02-15
    // Caching should be handled by React Query, not manual cache
    const data = await getDashboardOverview();
    return data;
  }

  async getDashboardMetrics(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const url = queryString ? `${this.endpoints.dashboard.metrics}?${queryString}` : this.endpoints.dashboard.metrics;
    return this.client.get(url);
  }

  // Project methods
  async getProjects(filters = {}) {
    // Adapter: backend returns { jobsites: [...] }
    const queryString = new URLSearchParams(filters).toString();
    const url = queryString ? `${this.endpoints.projects.list}?${queryString}` : this.endpoints.projects.list;
    const resp = await this.client.get(url);
    if (resp && Array.isArray(resp.jobsites)) return resp.jobsites;
    return resp;
  }

  async getProject(id) {
    return this.client.get(this.endpoints.projects.get(id));
  }

  async createProject(projectData) {
    return this.client.post(this.endpoints.projects.create, projectData);
  }

  /** PM `Project` document at `/api/projects` (not a jobsite). */
  async createPmProject(projectData) {
    return this.client.post(this.endpoints.pm.projects.create, projectData);
  }

  async updateProject(id, updates) {
    return this.client.patch(this.endpoints.projects.update(id), updates);
  }

  async getProjectSafety(projectId) {
    return this.client.get(this.endpoints.projects.safety(projectId));
  }

  // Safety methods
  async getIncidents(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const url = queryString ? `${this.endpoints.safety.incidents}?${queryString}` : this.endpoints.safety.incidents;
    return this.client.get(url);
  }

  async reportIncident(incidentData) {
    const response = await this.client.post(this.endpoints.safety.incidents, incidentData);

    // @VH_DELETE_CANDIDATE: clearCache removed - use React Query invalidation | delete_after=2026-02-15
    // Cache invalidation should be handled by React Query
    // await this.client.clearCache();

    return response;
  }

  async getHazards(projectId = null) {
    const queryString = projectId ? `?projectId=${projectId}` : '';
    const url = `${this.endpoints.safety.hazards}${queryString}`;
    return this.client.get(url);
  }

  async reportHazard(hazardData) {
    return this.client.post(this.endpoints.safety.hazards, hazardData);
  }

  // Worker methods
  async getWorkers(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const url = queryString ? `${this.endpoints.workers.list}?${queryString}` : this.endpoints.workers.list;
    return this.client.get(url);
  }

  async getWorker(id) {
    return this.client.get(this.endpoints.workers.get(id));
  }

  async updateWorker(id, updates) {
    return this.client.patch(this.endpoints.workers.update(id), updates);
  }

  async getWorkerCertifications(workerId) {
    return this.client.get(this.endpoints.workers.certifications(workerId));
  }

  // Compliance methods
  async getComplianceRequirements(projectId = null) {
    const queryString = projectId ? `?projectId=${projectId}` : '';
    const url = `${this.endpoints.compliance.requirements}${queryString}`;
    return this.client.get(url);
  }

  async getComplianceAudits(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const url = queryString ? `${this.endpoints.compliance.audits}?${queryString}` : this.endpoints.compliance.audits;
    return this.client.get(url);
  }

  async submitAudit(auditData) {
    return this.client.post(this.endpoints.compliance.audits, auditData);
  }

  // Analytics methods
  async getSafetyAnalytics(timeRange = '30d', projectId = null) {
    const now = new Date();
    const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return getSafetyAnalytics({ start: start.toISOString(), end: now.toISOString() });
  }

  async getComplianceAnalytics(timeRange = '30d') {
    const url = `${this.endpoints.analytics.compliance}?timeRange=${timeRange}`;
    return this.client.get(url);
  }

  async getComplianceDashboardSummary() {
    return this.client.get(this.endpoints.complianceReports.dashboardSummary);
  }

  async exportAnalytics(type, filters = {}) {
    const response = await this.client.post(this.endpoints.analytics.export, {
      type,
      filters,
    });

    // Handle file download
    if (response.downloadUrl) {
      window.open(response.downloadUrl, '_blank');
    }

    return response;
  }

  // Report methods
  async generateReport(type, parameters = {}) {
    return this.client.post(this.endpoints.reports.generate, {
      type,
      parameters,
    });
  }

  async getReports(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const url = queryString ? `${this.endpoints.reports.list}?${queryString}` : this.endpoints.reports.list;
    return this.client.get(url);
  }

  async downloadReport(reportId) {
    // @VH_DELETE_CANDIDATE: Blob download needs refactoring - canonical client doesn't support responseType | delete_after=2026-02-15
    // Note: Canonical client returns JSON. For blob downloads, use fetch directly or extend canonical client
    const response = await fetch(`${this.endpoints.reports.download(reportId)}`, {
      headers: {
        'Authorization': `Bearer ${await import('../../utils/authToken').then(m => m.getAuthTokenAsync())}`,
      },
    });
    const blob = await response.blob();

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `report_${reportId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Real-time connections
  subscribeToNotifications(callback) {
    // Prefer explicit WS URL if provided (production streaming service)
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = import.meta.env.VITE_WS_HOST || window.location.hostname;
    const wsPort = import.meta.env.VITE_WS_PORT || '8080';
    const explicitWsUrl = import.meta.env.VITE_WS_URL;

    // Default to dedicated streaming service (port 8080) when no override provided
    const wsUrl = explicitWsUrl || `${protocol}://${wsHost}:${wsPort}`;

    // @VH_DELETE_CANDIDATE: createWebSocket removed - use WebSocket directly | delete_after=2026-02-15
    // Canonical client doesn't have createWebSocket - use WebSocket API directly
    const ws = new WebSocket(wsUrl);

    // On open: subscribe to core safety topics
    ws.onopen = () => {
      try {
        const topics = ['safety-alerts', 'risk-predictions', 'safety-events', 'system-metrics'];
        ws.send(JSON.stringify({ type: 'subscribe', topics }));
      } catch (_e) {
        // best-effort
      }
    };

    // Normalize streaming messages for existing dashboard handlers
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        // Streaming service wraps data as { type: 'stream-data', topic, data, timestamp }
        if (msg && msg.topic && Object.prototype.hasOwnProperty.call(msg, 'data')) {
          const mappedType =
            msg.topic === 'safety-alerts'
              ? 'safety_alert'
              : msg.topic === 'risk-predictions'
                ? 'risk_prediction'
                : msg.topic.replace(/-/g, '_');
          callback({
            type: mappedType,
            payload: msg.data,
            topic: msg.topic,
            timestamp: msg.timestamp,
          });
          return;
        }
        // Fallback: pass-through
        callback(msg);
      } catch (_e) {
        // Ignore parse errors silently to keep socket alive
      }
    };

    return ws;
  }

  subscribeToProjectUpdates(projectId, callback) {
    // @VH_DELETE_CANDIDATE: createWebSocket removed - use WebSocket directly | delete_after=2026-02-15
    const ws = new WebSocket(
      `${this.endpoints.realtime.updates}?projectId=${projectId}`
    );

    ws.onmessage = (event) => {
      const update = JSON.parse(event.data);
      callback(update);
    };

    return ws;
  }

  // File management
  async uploadFile(file, metadata = {}, onProgress) {
    // @VH_DELETE_CANDIDATE: uploadFile removed - use FormData with canonical client | delete_after=2026-02-15
    const formData = new FormData();
    formData.append('file', file);
    Object.keys(metadata).forEach((key) => {
      formData.append(key, metadata[key]);
    });

    // Canonical client doesn't have uploadFile - use post with FormData
    // Note: Progress tracking would need to be handled differently
    return this.client.post(this.endpoints.files.upload, formData);
  }

  async getFiles(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const url = queryString ? `${this.endpoints.files.list}?${queryString}` : this.endpoints.files.list;
    return this.client.get(url);
  }

  // Settings methods
  async getUserSettings() {
    return this.client.get(this.endpoints.settings.user);
  }

  async updateUserSettings(settings) {
    return this.client.patch(this.endpoints.settings.user, settings);
  }

  async getNotificationSettings() {
    return this.client.get(this.endpoints.settings.notifications);
  }

  async updateNotificationSettings(settings) {
    return this.client.patch(this.endpoints.settings.notifications, settings);
  }

  // Batch operations
  async batchUpdate(resources) {
    // @VH_DELETE_CANDIDATE: batchRequests removed - use Promise.all with canonical client | delete_after=2026-02-15
    // Canonical client doesn't have batchRequests - execute requests in parallel
    const requests = resources.map((resource) =>
      this.client.patch(`/api/${resource.type}/${resource.id}`, resource.updates)
    );
    return Promise.all(requests);
  }

  // Error recovery
  async retryFailedRequests() {
    // @VH_DELETE_CANDIDATE: processSyncQueue removed - vh-web is online-only | delete_after=2026-02-15
    // Offline queue sync removed - vh-web is online-only
    logger.warn('[IntegrationService] retryFailedRequests called but vh-web is online-only - no queue to process');
  }

  /* Health check - treat any 2xx as healthy; do not require specific JSON body */
  async checkApiHealth() {
    try {
      const response = await this.client.get('/health');
      // Canonical client returns data directly (no response.data)
      const status = 200; // If we got here, request succeeded
      const healthy = true;
      return {
        healthy,
        status,
        timestamp: response?.timestamp,
        version: response?.version,
      };
    } catch (error) {
      const status = error?.status || 0; // ApiError has status property
      const message = error?.message ?? 'Unknown error';
      if (status !== undefined && status !== 0) {
        logger.error('API health check failed', { status, message });
      } else {
        logger.error('API health check failed', { message });
      }
      return {
        healthy: false,
        status,
        error: message,
      };
    }
  }
}

const integrationserviceInstance = new IntegrationService();
export default integrationserviceInstance;
